import { useState } from "react";
import { Menu, X, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import defaultLogo from "@/assets/logo-wtt.png";

const navLinks = [
  { label: "Home", href: "/" },
  { label: "Tours", href: "/tours" },
  { label: "Transfers", href: "/transfers" },
  { label: "Fleet", href: "/fleet" },
  { label: "Destinations", href: "/destinations" },
  { label: "About", href: "/about" },
  { label: "Contact", href: "/contact" },
];

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { getSetting } = useSiteSettings();

  const logoUrl = getSetting("header_logo_url") || defaultLogo;
  const siteTitle = getSetting("site_title", "World Transfers Trips");

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm border-b border-border/40">
      <div className="container mx-auto px-4 py-3 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <img
            src={logoUrl}
            alt={siteTitle}
            width={48}
            height={48}
            className="h-12 w-auto"
          />
          <span className="hidden sm:block font-display font-bold text-foreground text-lg tracking-tight">
            World Transfers Trips
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-1">
          {navLinks.map((link) => (
            <Link
              key={link.label}
              to={link.href}
              className={`relative px-4 py-2 text-sm font-medium transition-colors ${
                location.pathname === link.href
                  ? "text-secondary font-semibold"
                  : "text-foreground/70 hover:text-secondary"
              }`}
            >
              {link.label}
              {location.pathname === link.href && (
                <motion.div
                  layoutId="activeNav"
                  className="absolute bottom-0 left-2 right-2 h-0.5 bg-secondary rounded-none"
                  transition={{ type: "spring", stiffness: 380, damping: 30 }}
                />
              )}
            </Link>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-3">
          <Link to="/#booking">
            <Button
              size="sm"
              className="gap-2 font-semibold rounded-none px-6 bg-secondary text-secondary-foreground hover:bg-secondary/90"
            >
              <Phone className="w-4 h-4" />
              Book Now
            </Button>
          </Link>
        </div>

        <button
          className="lg:hidden text-foreground p-2 hover:bg-muted rounded-none transition-colors"
          onClick={() => setIsOpen(!isOpen)}
        >
          {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-border/40 overflow-hidden"
          >
            <div className="container mx-auto px-4 py-4 flex flex-col gap-1">
              {navLinks.map((link, index) => (
                <motion.div
                  key={link.label}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Link
                    to={link.href}
                    className={`block px-4 py-3 rounded-none text-sm font-medium transition-all ${
                      location.pathname === link.href
                        ? "text-secondary bg-secondary/5 font-semibold"
                        : "text-foreground/70 hover:text-secondary hover:bg-secondary/5"
                    }`}
                    onClick={() => setIsOpen(false)}
                  >
                    {link.label}
                  </Link>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="mt-3"
              >
                <Link to="/#booking" onClick={() => setIsOpen(false)}>
                  <Button
                    size="lg"
                    className="w-full gap-2 font-semibold rounded-none bg-secondary text-secondary-foreground hover:bg-secondary/90"
                  >
                    <Phone className="w-4 h-4" />
                    Book Now
                  </Button>
                </Link>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
